import React, { useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MARKET } from '../../constants';
import { VehicleDetails } from '../../types';
import { COLORS } from '../../styles/variables';
import { vehicleContext } from '../VehicleProvider';
import InfoPopover from '../InfoPopover';
import {
  StatusBlockContainer,
  StatusBlockContent,
  StatusDot,
  StatusTitle,
} from './StickyNavbar.styles';

const StatusBlock: React.FC = () => {
  const { t } = useTranslation();
  const parentElementRef = useRef<HTMLDivElement>(undefined as unknown as HTMLDivElement);

  const { vehicle } = useContext(vehicleContext);
  const { isAvailableNow, vehicleType, vehicleOrderStatus, saleOrderType } =
    (vehicle as VehicleDetails) ?? {};

  const isReservedVehicle = saleOrderType?.includes('SOLD');

  const statusForDot = useMemo(() => {
    if (isReservedVehicle) return 'REVERVED';
    if (isAvailableNow) return 'AVAILABLE';
    return 'PIPELINE';
  }, [isAvailableNow, isReservedVehicle]);

  const vehicleStatus = useMemo(() => {
    if (isReservedVehicle) return t('vehiclePricingBar.dealerListPrice.orderStatus.reserved');
    if (isAvailableNow) return t(`vehiclePricingBar.dealerListPrice.availableNow${MARKET}.label`);
    if (!isAvailableNow && vehicleType === 'new' && vehicleOrderStatus === 'Intransit From Factory')
      return t('vehiclePricingBar.dealerListPrice.orderStatus.factory');
    if (!isAvailableNow && vehicleType === 'new' && vehicleOrderStatus === 'Port Stock')
      return t('vehiclePricingBar.dealerListPrice.orderStatus.port');
    if (!isAvailableNow && vehicleType === 'new' && vehicleOrderStatus === 'Intransit Inland')
      return t('vehiclePricingBar.dealerListPrice.orderStatus.inland');
    return t('vehiclePricingBar.dealerListPrice.orderStatus.contactDealer');
  }, [isReservedVehicle, vehicleType, vehicleOrderStatus, isAvailableNow, t]);

  const statusDescription = useMemo(() => {
    if (isReservedVehicle)
      return t('vehiclePricingBar.dealerListPrice.reservedStatus.popoverDescription');
    if (isAvailableNow && MARKET === 'CAN')
      return t('vehiclePricingBar.dealerListPrice.availableNowCAN.popoverDescription');
    if (isAvailableNow && MARKET !== 'CAN')
      return t('vehiclePricingBar.dealerListPrice.availableNowUSA.popoverDescription');
    if (!isAvailableNow && MARKET === 'CAN')
      return t('vehiclePricingBar.dealerListPrice.availableSoon.popoverDescriptionCAN');
    if (!isAvailableNow && MARKET !== 'CAN')
      return t('vehiclePricingBar.dealerListPrice.availableSoon.popoverDescriptionUSA');
    return '';
  }, [isAvailableNow, isReservedVehicle, t]);

  if (!vehicle) return null;

  return (
    <>
      {vehicleType === 'new' && (
        <div ref={parentElementRef}>
          <StatusBlockContainer>
            <StatusBlockContent>
              <StatusDot statusForDot={statusForDot} />
              <StatusTitle data-testid="availability-status">{vehicleStatus}</StatusTitle>
              <InfoPopover
                description={statusDescription}
                iconSize={22}
                iconColor={COLORS.grey10}
                triggerTestId="availability-popover-trigger"
                parentElement={parentElementRef.current}
                popoverAlign="start"
              />
            </StatusBlockContent>
          </StatusBlockContainer>
        </div>
      )}
    </>
  );
};

export default StatusBlock;
